import React, { useState, useEffect } from 'react';
import { useGenerateSoleFromObjectsMutation, useCicdMutation } from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectProjectPath, selectBranch } from 'app/sharedSlice';
import { selectSnowflakeAccount, selectSnowflakeAccountLabel, selectSelectedDatabase } from 'app/snowflakeSlice';
import {
  selectSelectedDatabases,
  selectDatabase,
  selectSelectedSchemas,
  selectSelectedSchemaLevelObjects,
  selectDataProductName,
  setGenerateSoleFromObjectsOutput,
  setSoleOutput,
} from '../../data-product-builder/reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import SourceSelector from '../../data-product-builder/components/SourceSelector';

export interface DatabaseTablesStepProps {
  onBack: () => void;
  onContinue: () => void;
  skipGrants: boolean;
}

export default function DatabaseTablesStep({ onBack, onContinue, skipGrants }: DatabaseTablesStepProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [generateSoleFromObjects] = useGenerateSoleFromObjectsMutation();
  const [cicd] = useCicdMutation();
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const snowflakeAccountLabel = useAppSelector(selectSnowflakeAccountLabel);
  const selectedDatabase = useAppSelector(selectSelectedDatabase);
  const selectedDatabases = useAppSelector(selectSelectedDatabases);
  const selectedSchemas = useAppSelector(selectSelectedSchemas);
  const selectedSchemaLevelObjects = useAppSelector(selectSelectedSchemaLevelObjects);
  const dataProductName = useAppSelector(selectDataProductName);
  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Failed to generate config');

  useEffect(() => {
    dispatch(selectDatabase({ database: selectedDatabase }));
  }, [selectedDatabase]);

  const handleContinue = (): void => {
    setLoading(true);
    setFailed(false);
    const databaseToSend = selectedDatabases.map((database) => database.database);
    const schemasToSend = selectedSchemas.map((schema) => {
      return { database: schema.database, object_id: schema.schema };
    });
    const tablesToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'table')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const viewsToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'view')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const functionsToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'function')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const proceduresToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'procedure')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const fileFormatsToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'file_format')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const sequencesToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'sequence')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const pipesToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'pipe')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    generateSoleFromObjects({
      account: snowflakeAccount,
      databases: databaseToSend,
      schemas: schemasToSend,
      tables: tablesToSend,
      views: viewsToSend,
      functions: functionsToSend,
      procedures: proceduresToSend,
      file_formats: fileFormatsToSend,
      sequences: sequencesToSend,
      pipes: pipesToSend,
      skipGrants,
    })
      .unwrap()
      .then((res: any) => {
        const dbtSole = res;
        dispatch(setGenerateSoleFromObjectsOutput(dbtSole));
        cicd({
          dbtSole,
          dataProductName,
          projectName: projectPath,
          newDatabaseName: selectedDatabase,
          account: snowflakeAccount,
          branch,
        })
          .unwrap()
          .then((soleRes: any) => {
            dispatch(setSoleOutput(soleRes));
            onContinue();
          })
          .catch((soleErr) => {
            console.log(soleErr);
            setFailed(true);
            setErrorMessage(soleErr.data);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch((err) => {
        console.log(err);
        setErrorMessage(err.data);
        setFailed(true);
        setLoading(false);
      });
  };

  return (
    <StepWrapper
      title="Which Snowflake objects would you like to generate configurations for?"
      subtitle="Select Snowflake objects"
      onBack={() => onBack()}
      onContinue={() => handleContinue()}
      continueDisabled={
        (selectedDatabases.length === 0 && selectedSchemas.length === 0 && selectedSchemaLevelObjects.length === 0) ||
        loading
      }
      isLoading={loading}
      isFailed={failed}
      errorMessage={errorMessage}
    >
      <div className="w-full h-full flex flex-col items-center">
        <div className="flex w-full">
          <div className="w-1/2 h-full flex flex-col items-center">
            <div className="relative w-3/4 mb-3">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-400" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-sm text-gray-600">{snowflakeAccountLabel}</span>
              </div>
            </div>
            <SourceSelector
              mode="selection"
              objectTypes={['table', 'view', 'function', 'procedure', 'file_format', 'sequence', 'pipe']}
              databases={[selectedDatabase]}
            />
          </div>
          <div className="w-1/2 h-full flex flex-col items-center">
            <div className="relative w-3/4 mb-3">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-400" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-sm text-gray-600">{dataProductName}</span>
              </div>
            </div>
            <SourceSelector
              mode="preview"
              objectTypes={['table', 'view', 'function', 'procedure', 'file_format', 'sequence', 'pipe']}
              databases={[selectedDatabase]}
            />
          </div>
        </div>
        <div className="min-w-[20rem] mx-[auto] mt-4">
          <LoadingAndErrorSection
            isLoading={loading}
            isFailed={failed}
            errorMessage={errorMessage}
            hideLoading={true}
          />
        </div>
      </div>
    </StepWrapper>
  );
}
